import { mainIconSize } from '@/constants';
import type { SampleWithNamesPublic } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useRouter } from '@tanstack/react-router';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.css';

export type SampleNameCellRendererProps = ICellRendererParams<SampleWithNamesPublic, string>;

const SampleNameCellRenderer: FC<SampleNameCellRendererProps> = ({ value, node, data }) => {
  const { t } = useTranslation(['studies']);
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState<boolean>(!!node.expanded);

  useEffect(() => {
    const handleExpandedChanged = () => {
      setIsExpanded(!!node.expanded);
    };

    node.addEventListener('expandedChanged', handleExpandedChanged);

    return () => {
      node.removeEventListener('expandedChanged', handleExpandedChanged);
    };
  }, [node]);

  const isMaster = node.master;

  return (
    <Tooltip
      as="div"
      triggerClassName={clsx(styles.tooltip, styles.nameCell)}
      content={isExpanded ? t('samples.collapseDetail', { name: value }) : t('samples.expandDetail', { name: value })}
      placement="top-end"
      trigger={
        <Stack alignItems="center" className={styles.nameCell} fullHeight fullWidth gap={4} orientation="horizontal">
          {isMaster &&
            (isExpanded ? (
              <ChevronUp aria-label="collapse detail" size={mainIconSize} />
            ) : (
              <ChevronDown aria-label="expand detail" size={mainIconSize} />
            ))}
          {data ? (
            <Button
              className={styles.btn}
              data-testid="sample-link-button"
              onPress={() => router.navigate({ to: '/samples/$sampleId', params: { sampleId: data.id } })}
              size="xs"
              visual="unstyled"
            >
              {value}
            </Button>
          ) : (
            value
          )}
        </Stack>
      }
    />
  );
};

export default memo(SampleNameCellRenderer);
